import { useCallback, useEffect, useRef, useState } from "react";

import {
  runSessionTidy,
  type SessionTidyInput,
  type SessionTidyItem,
  type SessionTidyOutcome,
} from "./sessionTidyClient";

/**
 * Can 80 (W-105e4e0a): the rail's running tidy, kept by the rail, not by its
 * dialog, so the dialog can close while the tidy goes on. The Desk runs the
 * tidy itself (sessionTidy.ts SessionTidyRuns); this asks it again with the
 * same operation until it is done, tells the rail each session archived on
 * the way (so it drops them as they go) and once when the tidy is done (so it
 * reads the World again). A dialog opened while it runs shows where it stands.
 */
export interface SessionTidyRunView {
  readonly worldKey: string;
  readonly operationId: string;
  /** The count the person approved. */
  readonly count: number;
  /** Archived so far. */
  readonly archived: number;
  /** The tidy's own count once it has confirmed its sessions. */
  readonly total: number | null;
  /** Null while the tidy runs. */
  readonly outcome: SessionTidyOutcome | null;
}

export interface SessionTidyRun {
  /** The tidy running, or finished and not yet dismissed, in this World; else null. */
  readonly run: SessionTidyRunView | null;
  /** Start a tidy. Nothing starts while one runs. */
  readonly start: (
    input: SessionTidyInput & { readonly operationId: string; readonly count: number },
  ) => void;
  /** Forget a finished tidy, so the next dialog previews afresh. A running one stays. */
  readonly dismiss: () => void;
}

export function useSessionTidyRun(input: {
  readonly worldKey: string | null | undefined;
  /** Sessions newly archived, as the tidy goes; each is told once. */
  readonly onArchived: (sessionIds: ReadonlyArray<string>) => void;
  /** The tidy finished, whatever it archived. */
  readonly onDone: (outcome: SessionTidyOutcome) => void;
}): SessionTidyRun {
  const [run, setRun] = useState<SessionTidyRunView | null>(null);
  // The latest callbacks, for a tidy that outlives the render that started it.
  const latest = useRef(input);
  useEffect(() => {
    latest.current = input;
  });
  const running = useRef(false);
  const start = useCallback<SessionTidyRun["start"]>((tidy) => {
    if (running.current) return;
    running.current = true;
    setRun({
      worldKey: tidy.worldKey,
      operationId: tidy.operationId,
      count: tidy.count,
      archived: 0,
      total: null,
      outcome: null,
    });
    const told = new Set<string>();
    const tell = (archived: ReadonlyArray<SessionTidyItem>) => {
      const fresh = archived.map((item) => item.sessionId).filter((id) => !told.has(id));
      for (const id of fresh) told.add(id);
      if (fresh.length > 0) latest.current.onArchived(fresh);
    };
    const same = (current: SessionTidyRunView | null) =>
      current !== null && current.operationId === tidy.operationId;
    void runSessionTidy(tidy, undefined, (progress) => {
      tell(progress.archived);
      setRun((current) =>
        same(current)
          ? { ...current!, archived: progress.archived.length, total: progress.total }
          : current,
      );
    }).then((outcome) => {
      running.current = false;
      setRun((current) =>
        same(current)
          ? {
              ...current!,
              archived: outcome.ok ? outcome.archived.length : current!.archived,
              outcome,
            }
          : current,
      );
      latest.current.onDone(outcome);
    });
  }, []);
  const dismiss = useCallback(() => {
    setRun((current) => (current !== null && current.outcome !== null ? null : current));
  }, []);
  const worldKey = input.worldKey;
  return { run: run !== null && run.worldKey === worldKey ? run : null, start, dismiss };
}
